import { lazy, Suspense, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth, SignIn, UserButton } from "@clerk/react";
import { clientsApi, casesApi, tenantsApi, DOC_STATUS_LABEL } from "./api.js";
import { isClerkEnabled, setAuthTokenGetter } from "./lib/clerkAuth.js";
import { StatusBadge } from "./components/StatusBadge.js";
import { ToastProvider } from "./components/Toast.js";
import { AIPanel } from "./components/ai/AIPanel.js";
import { useAIStore } from "./stores/aiStore.js";
import { ClientsPage } from "./pages/ClientsPage.js";
import { ClientDetailPage } from "./pages/ClientDetailPage.js";
import { CasePage } from "./pages/CasePage.js";
import { DocumentPage } from "./pages/DocumentPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { WorkItemMasterPage } from "./pages/WorkItemMasterPage.js";
import { ProfessionMasterPage } from "./pages/ProfessionMasterPage.js";
import { FeeSchedulePage } from "./pages/FeeSchedulePage.js";
import { TemplatesPage } from "./pages/TemplatesPage.js";
import { TemplateNewPage } from "./pages/TemplateNewPage.js";
import { TemplateEditPage } from "./pages/TemplateEditPage.js";
import { TemplateLibraryPage } from "./pages/TemplateLibraryPage.js";
import { CasePdfsPage } from "./pages/CasePdfsPage.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { useScreenView } from "./lib/analytics.js";

// ── ダッシュボード ─────────────────────────────────────────────────────────
function DashboardPage() {
  useScreenView("dashboard");
  const { data: cases } = useQuery({ queryKey: ["cases"], queryFn: () => casesApi.list() });
  const { data: clients } = useQuery({ queryKey: ["clients"], queryFn: () => clientsApi.list() });
  const navigate = useNavigate();

  const activeCases   = cases?.data.filter((c) => c.status === "active").length ?? 0;
  const totalClients  = clients?.total ?? 0;

  const recentCases = cases?.data.slice(0, 8) ?? [];

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, marginBottom: 20 }}>ダッシュボード</h1>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { label: "進行中の案件", value: activeCases,  warn: activeCases > 0 },
          { label: "顧客数",       value: totalClients, warn: false },
          { label: "年度",         value: `令和${new Date().getFullYear() - 2018}年`, warn: false, str: true },
        ].map((card) => (
          <div key={card.label} style={{
            background: card.warn ? "#fff7ed" : "#fff",
            border: `1px solid ${card.warn ? "#fed7aa" : "#e2e8f0"}`,
            borderRadius: 12,
            padding: "16px 20px",
          }}>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{card.label}</div>
            <div style={{ fontSize: card.str ? 22 : 32, fontWeight: 800, marginTop: 8, color: card.warn ? "#c2410c" : "#0f172a" }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>最近の案件</h2>
        <button onClick={() => navigate("/clients")} style={btnStyle("ghost")}>顧客管理へ →</button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
            {["顧客名", "年度", "ステータス", "作成日"].map((h) => (
              <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {recentCases.map((c) => (
            <tr key={c.id} style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                onClick={() => navigate(`/cases/${c.id}`)}>
              <td style={{ padding: "10px 12px", fontWeight: 500 }}>
                {c.client_name}
                {c.title && <span style={{ fontSize: 12, color: "#64748b", marginLeft: 6 }}>{c.title}</span>}
              </td>
              <td style={{ padding: "10px 12px", color: "#64748b" }}>令和{c.fiscal_year}年</td>
              <td style={{ padding: "10px 12px" }}><StatusBadge status={c.status} /></td>
              <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12 }}>
                {new Date(c.created_at).toLocaleDateString("ja-JP")}
              </td>
            </tr>
          ))}
          {recentCases.length === 0 && (
            <tr><td colSpan={4} style={{ padding: "24px 12px", textAlign: "center", color: "#94a3b8" }}>案件がありません</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── ボタンスタイル ──────────────────────────────────────────────────────────
export function btnStyle(variant: "primary" | "danger" | "ghost" | "outline"): CSSProperties {
  const base: CSSProperties = { padding: "7px 14px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none" };
  switch (variant) {
    case "primary": return { ...base, background: "#0f766e", color: "#fff" };
    case "danger":  return { ...base, background: "#dc2626", color: "#fff" };
    case "ghost":   return { ...base, background: "transparent", color: "#0f766e" };
    case "outline": return { ...base, background: "#fff", color: "#334155", border: "1px solid #cbd5e1" };
  }
}

// ── サイドバーナビ ──────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { to: "/",                    label: "ダッシュボード", end: true },
  { to: "/clients",             label: "顧客・案件管理", end: false },
  { to: "/settings/templates",  label: "テンプレート管理", end: false },
  { to: "/settings",            label: "設定",           end: true },
];

function HamburgerButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        width: 34,
        height: 34,
        borderRadius: 7,
        border: "1px solid #cbd5e1",
        background: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span aria-hidden="true" style={{ display: "grid", gap: 4 }}>
        <span style={{ width: 16, height: 2, background: "#334155", borderRadius: 999 }} />
        <span style={{ width: 16, height: 2, background: "#334155", borderRadius: 999 }} />
        <span style={{ width: 16, height: 2, background: "#334155", borderRadius: 999 }} />
      </span>
    </button>
  );
}

function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { data: tenant } = useQuery({ queryKey: ["tenant", "me"], queryFn: () => tenantsApi.getMe() });

  if (collapsed) {
    return (
      <nav style={{
        width: 56, height: "100%", background: "#fff",
        borderRight: "1px solid #e2e8f0", padding: "12px 10px",
        display: "flex", flexDirection: "column", alignItems: "center",
        flexShrink: 0, overflowY: "auto",
      }}>
        <HamburgerButton onClick={onToggle} label="サイドメニューを表示" />
      </nav>
    );
  }

  return (
    <nav style={{
      width: 220, height: "100%", background: "#fff",
      borderRight: "1px solid #e2e8f0", padding: "20px 12px",
      display: "flex", flexDirection: "column", gap: 4,
      flexShrink: 0, overflowY: "auto",
    }}>
      <div style={{ padding: "0 10px 16px", borderBottom: "1px solid #e2e8f0", marginBottom: 12, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <HamburgerButton onClick={onToggle} label="サイドメニューを畳む" />
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", wordBreak: "break-word" }}>{tenant?.name ?? " "}</div>
      </div>
      {NAV_ITEMS.map(({ to, label, end }) => (
        <NavLink key={to} to={to} end={end} style={({ isActive }) => ({
          display: "block", padding: "9px 12px", borderRadius: 8, fontSize: 14,
          fontWeight: isActive ? 600 : 500,
          color: isActive ? "#0f766e" : "#334155",
          background: isActive ? "#f0fdfa" : "transparent",
          textDecoration: "none",
          borderLeft: isActive ? "3px solid #0f766e" : "3px solid transparent",
          flexShrink: 0,
        })}>
          {label}
        </NavLink>
      ))}
      {isClerkEnabled && (
        <div style={{ marginTop: "auto", marginBottom: 24, paddingTop: 12, borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <UserButton />
          <span style={{ fontSize: 12, color: "#64748b" }}>アカウント</span>
        </div>
      )}
    </nav>
  );
}

// ── 管理者コンソール（運用者専用・code-split。顧客向けナビからはリンクしない） ──
const AdminApp = lazy(() => import("./admin/AdminApp.js"));

// ── Clerk トークンブリッジ（SignedIn 時のみマウント） ───────────────────────
// api.ts / workItemsApi.ts の request() は React コンテキスト外にあるため、
// useAuth().getToken をモジュールレベルのゲッターとして登録する。
function ClerkTokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    return () => setAuthTokenGetter(null);
  }, [getToken]);
  return null;
}

// ── Clerk ゲート（未サインイン時はサインイン画面、サインイン済みは既存レイアウト） ──
function ClerkGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f8fafc", color: "#64748b", fontSize: 13 }}>
        読み込み中...
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f8fafc" }}>
        <SignIn routing="hash" />
      </div>
    );
  }

  return (
    <>
      <ClerkTokenBridge />
      {children}
    </>
  );
}

// ── 既存レイアウト（Sidebar + Routes + AIPanel） ────────────────────────────
function AppLayout() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const isAIPanelOpen = useAIStore((state) => state.isPanelOpen);
  const aiPanelWidth = useAIStore((state) => state.panelWidth);

  return (
    <ToastProvider>
      <div style={{ display: "flex", fontFamily: "'Yu Gothic UI', 'Meiryo', sans-serif", height: "100vh", overflow: "hidden" }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <Sidebar collapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed((value) => !value)} />
        <main style={{ flex: 1, minWidth: 0, height: "100%", background: "#f8fafc", overflowY: "auto", marginRight: isAIPanelOpen ? aiPanelWidth : 0, transition: "margin-right 160ms ease" }}>
          <ErrorBoundary>
          <Routes>
            <Route path="/"               element={<DashboardPage />} />
            <Route path="/clients"        element={<ClientsPage />} />
            <Route path="/clients/:id"    element={<ClientDetailPage />} />
            <Route path="/cases/:id"      element={<CasePage />} />
            <Route path="/cases/:id/pdfs" element={<CasePdfsPage />} />
            <Route path="/documents/:id"  element={<DocumentPage />} />
            <Route path="/settings"                   element={<SettingsPage />} />
            <Route path="/settings/professions"       element={<ProfessionMasterPage />} />
            <Route path="/settings/work-items"        element={<WorkItemMasterPage />} />
            <Route path="/settings/fee-schedules"     element={<FeeSchedulePage />} />
            <Route path="/settings/templates"         element={<TemplatesPage />} />
            <Route path="/settings/templates/new"     element={<TemplateNewPage />} />
            <Route path="/settings/templates/library" element={<TemplateLibraryPage />} />
            <Route path="/settings/templates/:id"     element={<TemplateEditPage />} />
          </Routes>
          </ErrorBoundary>
        </main>
        <AIPanel />
      </div>
    </ToastProvider>
  );
}

// ── App ────────────────────────────────────────────────────────────────────
export function App() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/admin")) {
    return (
      <Suspense fallback={null}>
        <AdminApp />
      </Suspense>
    );
  }

  // isClerkEnabled は起動時に固定される定数（.env.local 由来）なので、
  // レンダーごとに Clerk フックを呼ぶかどうかが切り替わることはない
  // （React の Hooks ルール違反にならない）。
  if (isClerkEnabled) {
    return (
      <ClerkGate>
        <AppLayout />
      </ClerkGate>
    );
  }

  return <AppLayout />;
}
